import { useEffect, useRef, useState } from "react";
import { toEditable, fromEditable } from "./visualEdit";

/**
 * The company design, editable in place (2026-10-27). Click any words of the header, greeting,
 * sign-off or footer and type; the message slot is a locked box. Every edit comes back through
 * `onChange` as the design's HTML (placeholders intact).
 *
 * The page is reloaded only when the HTML changes from outside (the HTML tab, another widget) —
 * never for the admin's own typing, so the caret stays where it is.
 */
export default function VisualWidgetEditor({ html, onChange, minHeight = 420 }) {
  const frameRef = useRef(null);
  const ownHtml = useRef(null);            // the last HTML this editor itself produced
  const [page, setPage] = useState(() => toEditable(html));
  const [height, setHeight] = useState(minHeight);

  useEffect(() => {
    if (html !== ownHtml.current) setPage(toEditable(html));
  }, [html]);

  const fit = (doc) => setHeight(Math.max(minHeight, (doc.body?.scrollHeight || 0) + 24));

  const onLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.contentEditable = "true";
    doc.body.spellcheck = false;
    // Links in a design are not followed while editing it.
    doc.addEventListener("click", (e) => { if (e.target.closest?.("a")) e.preventDefault(); });
    doc.addEventListener("input", () => {
      const next = fromEditable(doc.documentElement.outerHTML);
      ownHtml.current = next;
      onChange?.(next);
      fit(doc);
    });
    fit(doc);
  };

  return (
    <iframe ref={frameRef} title="Widget visual editor" srcDoc={page} onLoad={onLoad}
      sandbox="allow-same-origin"
      style={{ width: "100%", height, border: "none", display: "block", background: "#eef0f7" }} />
  );
}
